import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Validate email format - checks for @ symbol and basic structure
export function isValidEmail(email: string): boolean {
  if (!email || !email.trim()) return false
  // Basic email regex that checks for: something@something.something
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

// Sanitize phone number input - only allow digits and common phone characters (+, -, (, ), space)
export function sanitizePhoneNumber(value: string): string {
  // Remove any character that is not a digit, +, -, (, ), or space
  return value.replace(/[^0-9+\-() ]/g, '')
}

// Validate phone number - must contain only digits and common phone characters
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone || !phone.trim()) return true // Empty is valid (optional field)
  // Check if it only contains valid phone characters
  const phoneRegex = /^[0-9+\-() ]+$/
  return phoneRegex.test(phone.trim())
}

// Validate a value against a regex pattern
export function validateRegex(value: string, pattern: string): boolean {
  if (!pattern) return true
  try {
    const regex = new RegExp(pattern)
    return regex.test(value)
  } catch {
    return false
  }
}

// Validate a regex pattern is valid
export function isValidRegexPattern(pattern: string): boolean {
  if (!pattern) return true
  try {
    new RegExp(pattern)
    return true
  } catch {
    return false
  }
}

// Format date for display
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Format date for input
export function formatDateForInput(dateString: string): string {
  const date = new Date(dateString)
  return date.toISOString().split('T')[0]
}

// Format currency for display
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

// Format number for display
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

// Get currency symbol from currency code
export function getCurrencySymbol(currencyCode: string = 'USD'): string {
  try {
    // Format 0 with the currency and extract just the symbol
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0)

    // Remove the number and any spaces to get just the symbol
    return formatted.replace(/\d/g, '').trim()
  } catch {
    // Fallback to currency code if formatting fails
    return currencyCode
  }
}

// Extract user-friendly error message from API error response
export function getApiErrorMessage(error: unknown, fallbackMessage: string = 'An error occurred'): string {
  if (!error) return fallbackMessage

  // Handle axios error response
  const axiosError = error as {
    response?: {
      data?: {
        message?: string;
        error?: string | {
          details?: string | { fields?: Record<string, string> };
          message?: string;
          type?: string;
        }
      }
    }
  }

  if (axiosError?.response?.data) {
    const data = axiosError.response.data

    // Handle error as an object (e.g., {type, details})
    if (typeof data.error === 'object' && data.error !== null) {
      // Check for field-level errors and format them
      if (typeof data.error.details === 'object' && data.error.details !== null && 'fields' in data.error.details) {
        const fields = (data.error.details as { fields: Record<string, string> }).fields
        const fieldErrors = Object.entries(fields)
          .filter(([_, msg]) => msg)
          .map(([_, msg]) => msg)

        if (fieldErrors.length > 0) {
          return fieldErrors.join('. ')
        }
      }

      // Check for details as a string
      if (typeof data.error.details === 'string' && data.error.details) {
        return data.error.details
      }

      // Check for message in error object
      if (data.error.message) {
        return data.error.message
      }
    }

    // Handle error as a string
    if (typeof data.error === 'string' && data.error) {
      return data.error
    }

    // Use the main message if available
    if (data.message) {
      return data.message
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message
  }

  return fallbackMessage
}

// Get field-level errors from API error response
export function getApiFieldErrors(error: unknown): Record<string, string> {
  if (!error) return {}

  const axiosError = error as { response?: { data?: { error?: { details?: { fields?: Record<string, string> } } } } }

  if (axiosError?.response?.data?.error?.details?.fields) {
    return axiosError.response.data.error.details.fields
  }

  return {}
}
